package com.essntl.uikit.components.button

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.icons.EsIcon
import com.essntl.uikit.components.icons.EsIconSize
import com.essntl.uikit.components.image.AsyncUrlIcon
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esTypography

@Composable
fun EsButton(
    onClick: () -> Unit,
    size: EsButtonSize,
    colors: EsButtonColors,
    text: String,
    border: BorderStroke? = null,
    prefixIconRes: String? = null,
    suffixIconRes: String? = null,
    prefixIconUrl: String? = null,
    suffixIconUrl: String? = null,
    shape: Shape = RoundedCornerShape(EsConstants.ButtonBorderRadius),
    enabled: Boolean = true,
    loading: Boolean = false,
    withFocusBorder: Boolean = true,
    height: Dp? = null,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    modifier: Modifier = Modifier,
) {
    val iconSize =
        remember(size) {
            when (size) {
                EsButtonSize.Large -> EsIconSize.Large
                EsButtonSize.Medium -> EsIconSize.Medium
                EsButtonSize.Small -> EsIconSize.Small
            }
        }

    EsButton(
        onClick = onClick,
        size = size,
        colors = colors,
        text = text,
        border = border,
        prefix =
            when {
                prefixIconRes != null -> {
                    {
                        EsIcon(
                            res = prefixIconRes,
                            size = iconSize,
                        )
                    }
                }

                prefixIconUrl != null -> {
                    {
                        AsyncUrlIcon(
                            url = prefixIconUrl,
                            modifier =
                                Modifier
                                    .size(iconSize.size),
                        )
                    }
                }

                else -> null
            },
        suffix =
            when {
                suffixIconRes != null -> {
                    {
                        EsIcon(
                            res = suffixIconRes,
                            size = iconSize,
                        )
                    }
                }

                suffixIconUrl != null -> {
                    {
                        AsyncUrlIcon(
                            url = suffixIconUrl,
                            modifier =
                                Modifier
                                    .size(iconSize.size),
                        )
                    }
                }

                else -> null
            },
        enabled = enabled,
        loading = loading,
        withFocusBorder = withFocusBorder,
        shape = shape,
        interactionSource = interactionSource,
        modifier = modifier,
    )
}

@Composable
fun EsButton(
    onClick: () -> Unit,
    size: EsButtonSize,
    colors: EsButtonColors,
    text: String,
    border: BorderStroke? = null,
    prefix: (@Composable () -> Unit)? = null,
    suffix: (@Composable () -> Unit)? = null,
    enabled: Boolean = true,
    loading: Boolean = false,
    withFocusBorder: Boolean = true,
    shape: Shape = RoundedCornerShape(EsConstants.ButtonBorderRadius),
    contentPadding: PaddingValues? = null,
    height: Dp? = null,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    modifier: Modifier = Modifier,
) {
    val height = height ?: remember(size) {
            when (size) {
                EsButtonSize.Large -> EsConstants.LargeButtonHeight
                EsButtonSize.Medium -> EsConstants.MediumButtonHeight
                EsButtonSize.Small -> EsConstants.SmallButtonHeight
            }
        }

    val spinnerSize =
        remember(size) {
            when (size) {
                EsButtonSize.Large -> EsSpinnerSize.Large
                EsButtonSize.Medium -> EsSpinnerSize.Medium
                EsButtonSize.Small -> EsSpinnerSize.Small
            }
        }

    val paddingValues = contentPadding ?: remember(size) {
        when (size) {
            EsButtonSize.Large -> PaddingValues(horizontal = 2.spacing)
            EsButtonSize.Medium -> PaddingValues(horizontal = 2.spacing)
            EsButtonSize.Small -> PaddingValues(horizontal = 1.5f.spacing)
        }
    }

    val esTypography = MaterialTheme.esTypography
    val textStyle =
        remember(size) {
            when (size) {
                EsButtonSize.Large -> esTypography.p1Medium16
                EsButtonSize.Medium -> esTypography.p1Medium16
                EsButtonSize.Small -> esTypography.c1Medium13
            }
        }

    val isFocused by interactionSource.collectIsFocusedAsState()
    val isHovered by interactionSource.collectIsHoveredAsState()
    val isPressed by interactionSource.collectIsPressedAsState()

    val containerColor =
        remember(
            enabled,
            isFocused,
            isHovered,
            isPressed,
        ) {
            when {
                !enabled -> colors.disabledContainerColor
                isPressed -> colors.pressedContainerColor
                isHovered -> colors.hoveredContainerColor
                isFocused -> colors.focusedContainerColor
                else -> colors.containerColor
            }
        }
    val textColor =
        remember(enabled) {
            if (enabled) colors.textColor else colors.disabledTextColor
        }
    val iconColor =
        remember(enabled) {
            if (enabled) colors.iconColor else colors.disabledIconColor
        }

    Box(
        contentAlignment = Alignment.Center,
        modifier =
            modifier
                .then(
                    if (isFocused && enabled && !isPressed && withFocusBorder) {
                        Modifier
                            .drawBehind {
                                // TODO: Fix this hacky way of drawing the border
                                drawRoundRect(
                                    color = Color.White,
                                    cornerRadius =
                                        CornerRadius(
                                            EsConstants.ButtonBorderRadius.toPx(),
                                            EsConstants.ButtonBorderRadius.toPx(),
                                        ),
                                    topLeft = Offset.Zero,
                                    size = this.size,
                                    style = Stroke(width = EsConstants.ButtonBorderWidth.toPx()),
                                )
                            }
                            .drawBehind {
                                drawRoundRect(
                                    color = colors.borderFocusColor,
                                    cornerRadius =
                                        CornerRadius(
                                            EsConstants.ButtonBorderRadius.toPx(),
                                            EsConstants.ButtonBorderRadius.toPx(),
                                        ),
                                    topLeft = Offset.Zero,
                                    size = this.size,
                                    style = Stroke(width = EsConstants.ButtonBorderWidth.toPx()),
                                )
                            }
                    } else {
                        Modifier
                    },
                )
                .surface(
                    shape = shape,
                    backgroundColor = containerColor,
                    border = if ((isFocused && enabled && !isPressed) || !enabled || loading) null else border,
                    shadowElevation = 0.dp,
                )
                .pointerHoverIcon(
                    icon = if (enabled && !loading) PointerIcon.Hand else PointerIcon.Default,
                )
                .clickable(
                    interactionSource = interactionSource,
                    indication = null,
                    enabled = enabled && !loading,
                    onClick = onClick,
                    role = Role.Button,
                ),
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(1.spacing),
            verticalAlignment = Alignment.CenterVertically,
            modifier =
                Modifier
                    .graphicsLayer {
                        alpha = if (loading) 0f else 1f
                    }
                    .height(height)
                    .padding(paddingValues),
        ) {
            CompositionLocalProvider(
                LocalContentColor provides iconColor,
            ) {
                prefix?.invoke()
            }

            if (text.isNotEmpty())
                Text(
                    text = text,
                    style = textStyle,
                    color = textColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

            CompositionLocalProvider(
                LocalContentColor provides iconColor,
            ) {
                suffix?.invoke()
            }
        }

        if (loading) {
            EsSpinner(
                color = iconColor,
                size = spinnerSize,
                modifier =
                    Modifier
                        .align(Alignment.Center),
            )
        }
    }
}

internal fun Modifier.surface(
    shape: Shape,
    backgroundColor: Color,
    border: BorderStroke?,
    shadowElevation: Dp,
) = this.shadow(shadowElevation, shape, clip = false)
    .then(if (border != null) Modifier.border(border, shape) else Modifier)
    .background(color = backgroundColor, shape = shape)
    .clip(shape)

@Composable
private fun surfaceColorAtElevation(
    color: Color,
    elevation: Dp,
): Color {
    return if (color == MaterialTheme.colorScheme.surface) {
        MaterialTheme.colorScheme.surfaceColorAtElevation(elevation)
    } else {
        color
    }
}

class EsButtonColors(
    val containerColor: Color,
    val pressedContainerColor: Color,
    val focusedContainerColor: Color,
    val hoveredContainerColor: Color,
    val disabledContainerColor: Color,
    val textColor: Color,
    val iconColor: Color,
    val disabledTextColor: Color,
    val disabledIconColor: Color,
    val borderFocusColor: Color,
)

enum class EsButtonSize {
    Large,
    Medium,
    Small,
}
